import { C8DateRangeArray, DateOrTime } from "csi/typings";
import { Locale, isAfter, isValid, parse } from "date-fns";
import addDays from "date-fns/addDays";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import differenceInMinutes from "date-fns/differenceInMinutes";
import differenceInMonths from "date-fns/differenceInMonths";
import differenceInYears from "date-fns/differenceInYears";
import format from "date-fns/format";
import startOfWeek from "date-fns/startOfWeek";
import { C8DateRange } from "designSystem/components/dateRange/DateRange";
import dojoLocale from "dojo/date/locale";
import _ from "lodash";
import { DateRange as ReactDayPickerDateRange } from "react-day-picker";
import {
	FORMAT_DATE,
	FORMAT_DATETIME,
	FORMAT_DATETIME_SECONDS,
	FORMAT_DATETIME_ZONE,
	FORMAT_DATE_SHORT,
	FORMAT_DIFF,
	FORMAT_DIFF_YEAR, FORMAT_MONTH, FORMAT_SAME_DAY, FORMAT_SAME_YEAR,
	FORMAT_TIME,
	FORMAT_TIME_MEDIUM, FORMAT_TIME_SHORT,
	FORMAT_UNIVERSAL_DATE,
	FORMAT_UNIVERSAL_DATETIME,
	FORMAT_UNIVERSAL_TIME
} from "src/c8-platform/type/format";

export {
	FORMAT_DATE,
	FORMAT_DATETIME, FORMAT_DATETIME_SECONDS, FORMAT_DATETIME_ZONE, FORMAT_DATE_SHORT, FORMAT_DIFF, FORMAT_DIFF_YEAR,
	FORMAT_MONTH,
	FORMAT_SAME_DAY,
	FORMAT_SAME_YEAR, FORMAT_TIME, FORMAT_TIME_MEDIUM,
	FORMAT_TIME_SHORT, FORMAT_UNIVERSAL_DATE, FORMAT_UNIVERSAL_DATETIME,
	FORMAT_UNIVERSAL_TIME
};

type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

let _locale = "en";
let _dateFnsLocale: Locale;
let _datePattern: string = "M/d/yyyy";
let _timePattern: string = "h:mm a";
const _timeWithSecondPattern: string = "h:mm:ss a";

// index to first day of week for locale relative to Sunday
let _firstDayOfWeek: WeekStartsOn = 0;
let _showFullYear = false;

const _dateTimeSeparator: Record<string, string> = {};

export const DAY_IN_S = 86400;
export const DAY_IN_MS = DAY_IN_S * 1000;

export type TypeFormatOptions = Record<string, string | boolean | Date | number[]>;

// Object: mapping of time formats to options
const _timeFormatOptions: Record<string, TypeFormatOptions> = {
	[FORMAT_DATE]: { selector: "date", datePattern: undefined },
	[FORMAT_DATETIME]: { selector: "date,time", datePattern: undefined, timePattern: undefined },
	[FORMAT_DATETIME_SECONDS]: { datePattern: undefined, timePattern: undefined }, // this is filled in on locale initialization
	[FORMAT_DATETIME_ZONE]: { selector: "date,time", formatLength: "long" },
	[FORMAT_TIME]: { selector: "time", timePattern: undefined, clickableIncrement: "T00:30:00", visibleIncrement: "T24:00:00" },
	[FORMAT_DIFF]: null,
	[FORMAT_UNIVERSAL_DATE]: { selector: "date", datePattern: undefined },
	[FORMAT_UNIVERSAL_TIME]: { selector: "time", datePattern: undefined, clickableIncrement: "T00:30:00", visibleIncrement: "T24:00:00" },
	[FORMAT_UNIVERSAL_DATETIME]: { selector: "date,time", datePattern: undefined, timePattern: undefined },
};

// format option for intlDateFormat
const formatOptionINTL: Record<string, Record<string, string>> = {
	[FORMAT_SAME_YEAR]: { month: "short", day: "numeric" },
	[FORMAT_DATE_SHORT]: { dateStyle: "short" },
	[FORMAT_DIFF_YEAR]: { year: "numeric", month: "short", day: "numeric" },
	[FORMAT_MONTH]: { month: "short" },
	[FORMAT_SAME_DAY]: { day: "numeric" },
	[FORMAT_TIME_SHORT]: { timeStyle: "short" },
	[FORMAT_TIME_MEDIUM]: { timeStyle: "medium" },
	[FORMAT_DATETIME]: { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" },
	[FORMAT_DATETIME_SECONDS]: { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" },
	[FORMAT_DATETIME_ZONE]: { dateStyle: "full", timeStyle: "full" },
	[FORMAT_DIFF]: null,
	[FORMAT_DATE]: { year: "numeric", month: "numeric", day: "numeric" }, // use the date pattern
	[FORMAT_TIME]: { hour: "numeric", minute: "numeric" }, // use the time pattern
	[FORMAT_UNIVERSAL_DATE]: { year: "numeric", month: "numeric", day: "numeric" }, // use the date pattern
	[FORMAT_UNIVERSAL_TIME]: { hour: "numeric", minute: "numeric" },
	[FORMAT_UNIVERSAL_DATETIME]: { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" },
};

// keep the first day of week for all c8 supported locale and use when browser does not support it
const firstDayOfWeekLocaleMap: Record<string, number> = {
	af: 0, bg: 1, bn: 0, cs: 1, da: 1, de: 1, el: 1,
	en: 0, "en-GB": 1, es: 1, "es-419": 1, et: 1, fi: 1, fr: 1,
	ga: 1, hi: 0, hr: 1, hu: 1, it: 1, ja: 0, ko: 0, lb: 1,
	lt: 1, lv: 1, ms: 1, mt: 0, nb: 1, nl: 1, pl: 1, pt: 0,
	"pt-BR": 0, ro: 1, ru: 1, sk: 1, sl: 1, sv: 1, th: 0, tr: 1,
	vi: 1, zh: 0, "zh-TW": 0,
};

/** enum for date range selection */
export enum DynamicDates {
	Today = "DynamicDates_Today",
	Tomorrow = "DynamicDates_Tomorrow",
	TheNextDay = "DynamicDates_TheNextDay",
	Yesterday = "DynamicDates_Yesterday",
	ThePastDay = "DynamicDates_ThePastDay",
	OneWeekAgo = "DynamicDates_OneWeekAgo",
	OneWeekFromNow = "DynamicDates_OneWeekFromNow",
	OneMonthAgo = "DynamicDates_OneMonthAgo",
	OneMonthFromNow = "DynamicDates_OneMonthFromNow",
	NumberOfDaysAgo = "DynamicDates_NumberOfDaysAgo",
	NumberOfDaysFromNow = "DynamicDates_NumberOfDaysFromNow",
}

/**
 * Enumeration for data range selection options
 * @enum {number}
 */
export enum DateRangeSelection {
	ALL = 0,
	DAY = 1,
	TODAY = 2,
	PAST_WEEK = 3,
	PAST_MONTH = 4,
	CUSTOM = 5,
	BLANKS = 6,
	NONBLANKS = 7,
	PAST_24_HOURS = 8,
	TOMORROW = 9,
	YESTERDAY = 10,
	FUTURE_WEEK = 11,
	FUTURE_MONTH = 12,
	PAST_NUMBER_OF_DAYS = 13,
	FUTURE_NUMBER_OF_DAYS = 14,
};

/** map of dynamic dates to date range selection */
export const dynamicDatesToDateRangeSelectionMap: Record<string, DateRangeSelection> = {
	[DynamicDates.Today]: DateRangeSelection.TODAY,
	[DynamicDates.Tomorrow]: DateRangeSelection.TOMORROW,
	[DynamicDates.TheNextDay]: DateRangeSelection.TOMORROW,
	[DynamicDates.Yesterday]: DateRangeSelection.YESTERDAY,
	[DynamicDates.ThePastDay]: DateRangeSelection.YESTERDAY,
	[DynamicDates.OneWeekAgo]: DateRangeSelection.PAST_WEEK,
	[DynamicDates.OneWeekFromNow]: DateRangeSelection.FUTURE_WEEK,
	[DynamicDates.OneMonthAgo]: DateRangeSelection.PAST_MONTH,
	[DynamicDates.OneMonthFromNow]: DateRangeSelection.FUTURE_MONTH,
	[DynamicDates.NumberOfDaysAgo]: DateRangeSelection.PAST_NUMBER_OF_DAYS,
	[DynamicDates.NumberOfDaysFromNow]: DateRangeSelection.FUTURE_NUMBER_OF_DAYS,
};

export const DateRangeResources: Record<number, string> = {
	0: "SelectAll",
	2: "DateRangeToday",
	3: "DateRangePastWeek",
	4: "DateRangePastMonth",
	6: "Blanks",
	7: "NonBlanks",
	8: "DateRangePast24Hours",
};


const centricLocaleToDateFnsImportMap = {
	af: (): Promise<{ default: Locale }> => import("date-fns/locale/af"),
	bg: (): Promise<{ default: Locale }> => import("date-fns/locale/bg"),
	bn: (): Promise<{ default: Locale }> => import("date-fns/locale/bn"),
	cs: (): Promise<{ default: Locale }> => import("date-fns/locale/cs"),
	da: (): Promise<{ default: Locale }> => import("date-fns/locale/da"),
	de: (): Promise<{ default: Locale }> => import("date-fns/locale/de"),
	el: (): Promise<{ default: Locale }> => import("date-fns/locale/el"),
	en: (): Promise<{ default: Locale }> => import("date-fns/locale/en-US"),
	en_GB: (): Promise<{ default: Locale }> => import("date-fns/locale/en-GB"),
	es: (): Promise<{ default: Locale }> => import("date-fns/locale/es"),
	es_419: (): Promise<{ default: Locale }> => import("date-fns/locale/es"),
	et: (): Promise<{ default: Locale }> => import("date-fns/locale/et"),
	fi: (): Promise<{ default: Locale }> => import("date-fns/locale/fi"),
	fr: (): Promise<{ default: Locale }> => import("date-fns/locale/fr"),
	he: (): Promise<{ default: Locale }> => import("date-fns/locale/he"),
	hi: (): Promise<{ default: Locale }> => import("date-fns/locale/hi"),
	hr: (): Promise<{ default: Locale }> => import("date-fns/locale/hr"),
	hu: (): Promise<{ default: Locale }> => import("date-fns/locale/hu"),
	it: (): Promise<{ default: Locale }> => import("date-fns/locale/it"),
	ja: (): Promise<{ default: Locale }> => import("date-fns/locale/ja"),
	ko: (): Promise<{ default: Locale }> => import("date-fns/locale/ko"),
	lt: (): Promise<{ default: Locale }> => import("date-fns/locale/lt"),
	lv: (): Promise<{ default: Locale }> => import("date-fns/locale/lv"),
	mn: (): Promise<{ default: Locale }> => import("date-fns/locale/mn"),
	ms: (): Promise<{ default: Locale }> => import("date-fns/locale/ms"),
	nb: (): Promise<{ default: Locale }> => import("date-fns/locale/nb"),
	nl: (): Promise<{ default: Locale }> => import("date-fns/locale/nl"),
	pl: (): Promise<{ default: Locale }> => import("date-fns/locale/pl"),
	pt: (): Promise<{ default: Locale }> => import("date-fns/locale/pt"),
	pt_BR: (): Promise<{ default: Locale }> => import("date-fns/locale/pt-BR"),
	ro: (): Promise<{ default: Locale }> => import("date-fns/locale/ro"),
	ru: (): Promise<{ default: Locale }> => import("date-fns/locale/ru"),
	sk: (): Promise<{ default: Locale }> => import("date-fns/locale/sk"),
	sl: (): Promise<{ default: Locale }> => import("date-fns/locale/sl"),
	sv: (): Promise<{ default: Locale }> => import("date-fns/locale/sv"),
	th: (): Promise<{ default: Locale }> => import("date-fns/locale/th"),
	tr: (): Promise<{ default: Locale }> => import("date-fns/locale/tr"),
	vi: (): Promise<{ default: Locale }> => import("date-fns/locale/vi"),
	zh: (): Promise<{ default: Locale }> => import("date-fns/locale/zh-CN"),
	zh_TW: (): Promise<{ default: Locale }> => import("date-fns/locale/zh-TW"),
};

/** Dynamically loads a locale from date-fns based on the given Centric locale code. */
export async function loadDateFnsLocale(centricLocale: string): Promise<Locale> {
	const localeImport = centricLocaleToDateFnsImportMap[centricLocale];
	if (localeImport) {
		try {
			_dateFnsLocale = (await localeImport()).default;
			return _dateFnsLocale;
		} catch (error) {
			console.error("Failed to load the locale:", error);
			throw new Error(`Locale data for ${centricLocale} could not be loaded.`);
		}
	} else {
		throw new Error(`Locale ${centricLocale} is not supported.`);
	}
}

/** get date-fns locale */
export function getDateFnsLocale(): Locale {
	return _dateFnsLocale;
}

/** is the date range valid, taking range mode into consideration with a min and max year */
export function isDateRangeValid(fromDate: Date, toDate: Date, isRangeMode: boolean, minYear: number, maxYear: number): boolean {
	let toDateIsValid = true;

	const toYear = toDate?.getFullYear();
	if (!toYear || toYear < minYear || toYear > maxYear) {
		toDateIsValid = false;
	}

	if (isRangeMode && !toDateIsValid) {
		return false;
	}

	const fromYear = fromDate?.getFullYear();
	if (!fromYear || fromYear < minYear || fromYear > maxYear) {
		return false;
	}

	if (isAfter(fromDate, toDate)) {
		return false;
	}

	return true;
}

/** convert an array of numbers to a date range */
export function toDayPickerDateRange(dates: C8DateRange | undefined): ReactDayPickerDateRange {
	const newRange: ReactDayPickerDateRange = { from: null, to: null };
	if (!dates) {
		return newRange;
	}

	newRange.from = dates.fromDate;
	newRange.to = dates.toDate;
	return newRange;
}

/** is a certain time like 12:00 AM or 12:22 AM selected? if not rounded, round to next 30 mins below */
export function isTimeSelected(time: string, selectedTime: string): boolean {
	const convertedSelectedTime = convertTimeTo12HrFormat(selectedTime);
	if (convertedSelectedTime === time) {
		return true;
	}

	const [hr, min] = convertedSelectedTime.split(":");
	const minutes = parseInt(min.substring(0, 2), 10);
	const roundedMinutes = minutes < 30 ? "00" : "30";
	const roundedTime = `${hr}:${roundedMinutes} ${min.substring(3)}`;
	return roundedTime === time;
}

/** parse a date given the string, format, and locale */
export function parseDate(dateString: string, format: string): Date {
	const parsedDate = parse(dateString, format, new Date(), { locale: getDateFnsLocale() });
	if (!isValid(parsedDate)) {
		console.error(`Invalid date: ${dateString}`);
		return null;
	}
	return parsedDate;
}

/** get the hours and minutes of a date */
export function getHoursAndMinutes(date: Date): string {
	if (!date) {
		return "0:00";
	}
	const hours = date.getHours().toString()
		.padStart(2, "0");
	const minutes = date.getMinutes().toString()
		.padStart(2, "0");
	return `${hours}:${minutes}`;
};

/** take in 03:30, return 03:30 AM */
export function convertTimeTo12HrFormat(timeString: string): string {
	const [hours, minutes] = timeString.split(":").map(Number);
	const period = hours >= 12 ? "PM" : "AM";
	const adjustedHours = hours % 12 || 12;
	return `${adjustedHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/** these are the time slots for the day */
export function getTimeSlots(): string[] {
	const timeSlots = [];
	for (let i = 0; i < 24; i++) {
		const hour = i % 12 === 0 ? 12 : i % 12;
		const paddedHour = hour.toString().padStart(2, "0");
		const suffix = i < 12 ? "AM" : "PM";
		timeSlots.push(`${paddedHour}:00 ${suffix}`);
		timeSlots.push(`${paddedHour}:30 ${suffix}`);
	}
	return timeSlots;
}

/**
 * return date offset by number of days from given date
 */
export function addDaysToDate(dt: Date, days: number): Date {
	return addDays(dt, days);
}

/**
 * Return true if given date ranges are equal
 */
export function areDateRangeValuesEqual(value1: C8DateRangeArray, value2: C8DateRangeArray): boolean {
	const selection1 = getDateRangeSelection(value1);
	const selection2 = getDateRangeSelection(value2);

	// relative date ranges only need to be compared by their selection rather than full value;
	// the values constantly update based on the current time
	if (isDateRangeSelectionRelative(selection1)) {
		return selection1 === selection2;
	}

	return _.isEqual(value1, value2);
}

/**
 * Return date object for given day and time dates
 */
export function combineDateAndTime(dtDay: DateOrTime, dtTime: DateOrTime): Date {
	const dt: Date = getStartOfDay(dtDay);
	const timeDate = new Date(dtTime);
	dt.setHours(timeDate.getHours(), timeDate.getMinutes(), timeDate.getSeconds(), timeDate.getMilliseconds());
	return dt;
}

/**
 * function changes date's UTC value so that it represents the same time, but for local time
 * @param {DateOrTime} value - Date representation, biased to UTC time
 * @return {Date} same date as "value", but time shifted biased to local time
 */
export function shiftUTCDateToLocal(value: DateOrTime): Date {
	const dtValue = new Date(value);
	dtValue.setUTCMinutes(dtValue.getUTCMinutes() + dtValue.getTimezoneOffset());
	return dtValue;
}

/**
 * function changes date's local value so that it represents the same time, but for UTC time
 * @param {DateOrTime} value - Date representation, biased to local time
 * @return {Date} same date as "value", but time shifted biased to UTC time
 */
export function shiftLocalDateToUTC(value: DateOrTime): Date {
	const dtValue = new Date(value);
	dtValue.setUTCMinutes(dtValue.getUTCMinutes() - dtValue.getTimezoneOffset());
	return dtValue;
}

/**
 * function changes date range's value time zone from local time zone to UTC (-1 GMT) without changing date and time values
 * @param {number} value - milliseconds, unix time
 * @return {number[]} date range
 */
export function convertDateRangeToUTC(value: C8DateRangeArray): C8DateRangeArray {
	// reference getUTCDateTime
	return [
		shiftLocalDateToUTC(value[0])
			.getTime(),
		shiftLocalDateToUTC(value[1])
			.getTime(),
		value[2],
	];
}

/** date range selection is not custom */
export function dateRangeSelectionIsNotCustom(value: C8DateRangeArray): boolean {
	return getDateRangeSelection(value) != DateRangeSelection.CUSTOM;
}

/** date range selection is in the future */
export function dateRangeSelectionIsInTheFuture(value: C8DateRangeArray): boolean {
	const dateRangeSelection = getDateRangeSelection(value);
	if (!dateRangeSelection) {
		return false;
	}
	return [
		DateRangeSelection.TOMORROW,
		DateRangeSelection.FUTURE_WEEK,
		DateRangeSelection.FUTURE_MONTH,
		DateRangeSelection.FUTURE_NUMBER_OF_DAYS,
	].includes(dateRangeSelection);
}

/**
 * Return proper format based on the current locale with the specified selector and pattern
 */
function getDateTimeFormatBasedOnLocale(locale: string, selector: string, pattern: string, date: Date): string {
	const options = {
		fullYear: _showFullYear,
		locale,
		selector,
		datePattern: pattern ?? null,
		timePattern: pattern ?? null,
	};

	return dojoLocale.format(date, options);
}


/**
 * Format the date based on the format item
 */
function formatDate(dt: Date, formatItem: string): string {
	if (formatItem === FORMAT_TIME || formatItem === FORMAT_UNIVERSAL_TIME) {
		return getDateTimeFormatBasedOnLocale(_locale, "time", _timePattern, dt);
	} else if ((formatItem === FORMAT_DATE) || (formatItem === FORMAT_UNIVERSAL_DATE)) {
		return getDateTimeFormatBasedOnLocale(_locale, "date", _datePattern, dt);
	} else if (formatItem === FORMAT_DATETIME || formatItem === FORMAT_DATETIME_SECONDS || formatItem === FORMAT_UNIVERSAL_DATETIME) {
		const _dateTimeSeparator = getDateTimeSeparator(_locale);
		const options = getTimeFormatOptions(formatItem);
		return getDateTimeFormatBasedOnLocale(_locale, "date", options.datePattern + _dateTimeSeparator + options.timePattern, dt);
	} else {
		return formatDateIntl(dt, formatOptionINTL[formatItem], _locale);
	}
}

/**
 * Format the date based on the format item
 */
function formatDateIntl(dt: Date, formatOption: Record<string, string>, locale: string): string {
	return new Intl.DateTimeFormat(locale, formatOption)
		.format(dt);
}

/**
 *
 * convert number of milliseconds into string given format - make sure to support old formats
 */
export function formatTime(ms: number, format: string, extraOptions?: Record<string, string>): string {
	if (!ms) return "";

	// handle FORMAT_DATE, FORMAT_DATETIME, FORMAT_TIME, FORMAT_DATETIME_ZONE, FORMAT_UNIVERSAL_TIME
	format ||= FORMAT_DATE;

	if (format == FORMAT_UNIVERSAL_TIME) {
		const dtUTC = getUTCTimeDate(ms);
		return formatDate(dtUTC, format);
	}

	const dt = getTimeDate(ms, format);
	const options = formatOptionINTL[format];

	if (options) {
		if (extraOptions && extraOptions?.fullYear) {
			const fullYearOptions = _.clone(options);
			fullYearOptions.year = "numeric";
			return formatDateIntl(dt, fullYearOptions, _locale);
		} else {
			return formatDate(dt, format);
		}
	}

	// handle FORMAT_DIFF
	if (format == FORMAT_DIFF) {
		const now = new Date();

		if (isSameLocalDate(dt, now)) {
			return formatDate(dt, FORMAT_TIME);
		}

		if (isSameLocalYear(dt, now)) {
			return getDateInYearString(dt) + " " + formatDate(dt, FORMAT_TIME);
		}
		return getFormattedDateString(dt, FORMAT_DIFF_YEAR) + " " + formatDate(dt, FORMAT_TIME);
	}

	return getFormattedDateString(dt, FORMAT_DIFF_YEAR);
}


/**
 * return string that captures date as string based on current timezone
 */
export function getAbsoluteDateString(ms: number): string {
	const dt = getTimeDate(ms);
	return [dt.getFullYear(), dt.getMonth(), dt.getDate()].join(",");
}

/**
 * determine number of days between two dates (864000000 milliseconds in day)
 */
export function getDaysToDate(dt1: Date, dt2: Date): number {
	return Math.floor((dt2.getTime() - dt1.getTime()) / 86400000);
}

/**
 * Return number of days from current day to given date
 */
export function getDateDifference(to: Date): number {
	const now = new Date();
	now.setHours(0, 0, 0, 0);

	const dt = new Date(to.getTime());
	dt.setHours(0, 0, 0, 0);

	return differenceInCalendarDays(dt, now);
}

/**
 *  Return the number of months between the given dates
 */
export function getDateDifferenceInMinutes(dateLeft: Date, dateRight: Date): number {
	return differenceInMinutes(dateLeft, dateRight);
}

/**
 *  Return the number of months between the given dates
 */
export function getDateDifferenceInMonth(dateLeft: Date, dateRight: Date): number {
	return differenceInMonths(dateLeft, dateRight);
}

/**
 * Return the number of full years between the given dates.
 */
export function getDateDifferenceInYear(dateLeft: Date, dateRight: Date): number {
	return differenceInYears(dateLeft, dateRight);
}

/**
 * Return date including month and day
 */
export function getDateInYearString(dt: Date): string {
	return getFormattedDateString(dt, FORMAT_SAME_YEAR);
}

/**
 * return localized name for month
*/
export function getDateMonthString(dt: Date): string {
	return getFormattedDateString(dt, FORMAT_MONTH);
}

/**
 * get pattern string for Date format
 */
export function getDatePattern(bFusionCharts: boolean): string {
	let pattern = _datePattern;

	if (bFusionCharts) {
		// FusionCharts only accepts 3 formats, so map pattern to closest one
		// Reference: http://docs.fusioncharts.com/flex/charts/Contents/Widget_XML_API/GanttDateFormat.html
		const fc = {
			d: "dd/mm/yyyy",
			m: "mm/dd/yyyy",
			y: "yyyy/mm/dd",
		};
		pattern = fc[pattern.toLowerCase()
			.charAt(0)];
	}
	return pattern;
}

/**
 * Return date range value for given dates and selection, setting hours of dates to include the full days
 */
export function getDateRange(dt1: DateOrTime, dt2: DateOrTime, selection: DateRangeSelection): C8DateRangeArray {
	if (selection == DateRangeSelection.ALL) {
		return [];
	}

	if (!selection) {
		selection = DateRangeSelection.CUSTOM;
	}

	const bRelative = isDateRangeSelectionRelative(selection);

	if (!dt1) {
		// return range with just selection
		return new C8DateRange(null, null, selection).toArray;
	} else if (!dt2) {
		// if second date not specified, then assume same as first
		dt2 = getEndOfDay(dt1);
	}

	const startDay = getStartOfDay(dt1);
	const endDay = getStartOfDay(dt2);
	let start: Date;
	let end: Date;

	if (startDay.getTime() == endDay.getTime()) {
		// if single day range, then also store start and end times
		start = bRelative ? combineDateAndTime(0, dt1) : new Date(dt1);
		end = bRelative ? combineDateAndTime(0, dt2) : new Date(dt2);
	} else {
		start = new Date(dt1);
		end = new Date(dt2);
	}

	return [
		start ? start.getTime() : 0,
		end ? end.getTime() : 0,
		selection,
	];
}

/**
 * Return Date Range Selection localiszation resource name
 */
export function getDateRangeRes(resId: number): string {
	return DateRangeResources[resId];
}

/**
 * Return selection value for date range
 */
export function getDateRangeSelection(value: C8DateRangeArray): DateRangeSelection | undefined {
	return value?.[2];
}

/** Return number of days */
export function getNumberOfDays(value: C8DateRangeArray): number | undefined {
	return value?.[3];
}

/**
 * Returns time value in milliseconds of given date, optionally indicating if time is wanted at the start of the date (midnight)
 */
export function getDateTime(dt?: Date, startOfDay?: boolean): number {
	return startOfDay ? getStartOfDay(dt)
		.getTime() : (dt || new Date()).getTime();
}

/**
 * Return the separator of the date and time based on the locale
 */
export function getDateTimeSeparator(locale: string): string {
	if (!_dateTimeSeparator[locale]) {
		//  formatToParts return all the locale - specific tokens.
		/* structure of formatToParts returns -
			[ { type: "weekday", value: "Monday" }, { type: "literal", value: ", " },
			{ type: "month", value: "12" }, { type: "literal", value: "/" },
			{ type: "day", value: "17" }, { type: "literal", value: "/" },
			{ type: "year", value: "2012" }, { type: "literal", value: ", " },
			{ type: "hour", value: "3" }, { type: "literal", value: ":" },
			{ type: "minute", value: "00" }, { type: "literal", value: ":" },
			{ type: "second", value: "42" }, { type: "fractionalSecond", value: "000" },
			{ type: "literal", value: " " },{ type: "dayPeriod", value: "AM" },
			];
		*/
		const formatter: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat(locale, formatOptionINTL[FORMAT_DATETIME])
			.formatToParts(new Date(2021, 7, 1, 1, 1, 1));
		// iterate all the tokens and identify the date and time separator
		// skip the "literal" for date pattern and time pattern
		const dateTimeSeparator = formatter.map((e) => {
			const value = e.value.toString();
			if (e.type === "literal") {
				return (value === "/" || value === "-" || value === ":" || value === "." || value === " ") ? "" : value;
			}
		})
			.join("")
			.trim();
		_dateTimeSeparator[locale] = dateTimeSeparator + " ";
	}
	return _dateTimeSeparator[locale];
}

/**
 * Return date object with time reset to end (11:59:59 PM) of the given day, or today if none given
 */
export function getEndOfDay(dt?: DateOrTime): Date {
	const dtEnd = (dt == null) ? new Date() : new Date(dt);
	dtEnd.setHours(23, 59, 59, 0);
	return dtEnd;
}

/**
 * Convert date into string with given format
 */
export function getFormattedDateString(dt: Date, formatItem: string, pattern?: string): string {
	if (pattern) {
		return format(dt, pattern);
	} else {
		return formatDate(dt, formatItem);
	}
}

/**
 * Return date object with time reset to beginning of the given day, or today if none given
 */
export function getStartOfDay(dt?: DateOrTime): Date {
	const dtStart = (dt == null) ? new Date() : new Date(dt);
	dtStart.setHours(0, 0, 0, 0);
	return dtStart;
}

/**
 * Convert milliseconds value to Date object
 */
export function getTimeDate(ms: number, format?: string): Date {
	if (format == FORMAT_UNIVERSAL_DATE || format == FORMAT_UNIVERSAL_TIME || format == FORMAT_UNIVERSAL_DATETIME) {
		return getUTCTimeDate(ms);
	}
	return new Date(ms);
}

/**
 * return options needed to select time value - if FORMAT_DIFF is passed, then return FORMAT_DATETIME options
 *
 */
export function getTimeFormatOptions(format?: string): TypeFormatOptions {
	return _timeFormatOptions[format || FORMAT_DATE] || _timeFormatOptions[FORMAT_DATETIME];
}

/**
 *  return Object with "begin" and "end" indicating date range to query for given time;return range of [0,0] if dates are not set
 */
export function getTimeRange(format: string, date1: Date, date2?: Date): C8DateRangeArray {
	const dt1 = new Date(date1);
	let dt2: Date;
	if (!format || (format == FORMAT_DATE)) {
		// truncate to day precision
		if (dt1.getTime()) {
			dt1.setHours(0, 0, 0, 0);
		}

		dt2 = date2 ? new Date(date2) : new Date(dt1.getTime());

		if (dt2.getTime()) {
			dt2.setHours(24, 0, 0, 0);
		}
	} else {
		// truncate to minute precision
		// FIX: this assumes TIME/DATETIME formats only display to minute level
		if (dt1.getTime()) {
			dt1.setSeconds(0, 0);
		}

		dt2 = date2 ? new Date(date2) : new Date(dt1.getTime());

		if (dt2.getTime()) {
			dt2.setSeconds(60, 0);
		}
	}
	return [dt1.getTime(), dt2.getTime()];
}

/**
 * Returns epoch time for current time-zone without any conversions. Usually, when getting epoch time,
 * it will convert the date to UTC first then gets the milliseconds from 1970 Jan 1st. We're not doing the conversion here.
 * dt - optional Date to convert into epoch, if none given then a new Date now will be created.
 * hours - optional parameter to add hours onto the UTCDateTime.
 * number -  milliseconds from 1st Jan 1970 until the date passed in (includes the time-zone, so no conversion to UTC is done on the date)
 */
export function getUTCDateTime(dt?: Date, hours?: number): number {
	const dt2 = dt ? new Date(dt) : new Date();

	// have to get the timezone offset (e.g. for Turkey GMT+0300 it'd return -180)
	// and add it to the current minutes on current time zone minutes and it'll be UTC Date object.
	dt2.setTime(dt2.getTime() - (dt2.getTimezoneOffset() * 60 * 1000));

	return dt2.getTime() + hoursToMS(hours);
}

/**
 * Converts milliseconds value to Date object. It will set the epoch time to local time zone. e.g. epoch is for 1st Oct 2017, if utc=true is
 * passed, then it'll return 1st Oct 2017 (+03:00 Turkey) assuming user is in Turkey.
 */
export function getUTCTimeDate(ms: number): Date {
	const dt = new Date(ms);
	return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), dt.getUTCHours(), dt.getUTCMinutes(), dt.getUTCSeconds(), dt.getUTCMilliseconds());
}

/**
 * return date that starts the week based on locale
 */
export function getWeekStartDate(dt?: Date): Date {
	dt = dt || new Date();
	return startOfWeek(dt, { weekStartsOn: _firstDayOfWeek });
}


/**
 * Return milliseconds from hours.
 */
export function hoursToMS(hours: number): number {
	return hours ? hours * 60 * 60 * 1000 : 0;
}

/**
  * Return true if given date range's selection is blanks
  */
export function isDateRangeBlanks(value: C8DateRangeArray): boolean {
	return getDateRangeSelection(value) == DateRangeSelection.BLANKS;
}

/**
 * Checks given string to ensure it is in one of our DATE formats without time
 */
export function isDateOnlyFormat(format: string): boolean {
	return format == FORMAT_DATE || format == FORMAT_UNIVERSAL_DATE;
}

/**
 * Return true if given date range's selection is nonblanks
 */
export function isDateRangeNonBlanks(value: C8DateRangeArray): boolean {
	return getDateRangeSelection(value) == DateRangeSelection.NONBLANKS;
}

/**
 * Return true if given date range selection is relative (dates relative to today)
 */
export function isDateRangeSelectionRelative(selection: DateRangeSelection): boolean {
	switch (selection) {
		case DateRangeSelection.TODAY:
		case DateRangeSelection.PAST_WEEK:
		case DateRangeSelection.PAST_MONTH:
		case DateRangeSelection.PAST_24_HOURS:
		case DateRangeSelection.PAST_NUMBER_OF_DAYS:
		case DateRangeSelection.TOMORROW:
		case DateRangeSelection.YESTERDAY:
		case DateRangeSelection.FUTURE_WEEK:
		case DateRangeSelection.FUTURE_MONTH:
		case DateRangeSelection.FUTURE_NUMBER_OF_DAYS:
			return true;
	}
	return false;
}

/**
 * Return true if given value represents a date range
 */
export function isDateRangeValue(value: C8DateRangeArray): boolean {
	const n = getDateRangeSelection(value);
	return ((n >= DateRangeSelection.ALL) && (n <= DateRangeSelection.NONBLANKS)) || n == DateRangeSelection.PAST_24_HOURS;
}

/*
 * determine if given value is an invalid Date (parsed from invalid date string)
 */
export function isInvalidDate(value: any): boolean {
	return value instanceof Date && isNaN(value.getTime());
}

/**
 * return true if time format
 */
export function isTimeFormat(format: string): boolean {
	return format in _timeFormatOptions;
}

/**
 * return true if today matches given time
 */
export function isTimeToday(ms: number): boolean {
	const t1 = getDateTime(getTimeDate(ms), true);
	const t2 = getDateTime(null, true);
	return t1 == t2;
}

/**
 * If given date range is relative, then return new value with updated dates
 */
export function updateDateRange(value: C8DateRangeArray, isolateDay?: boolean, startOfEndDay?: boolean): C8DateRangeArray {
	const selection = getDateRangeSelection(value);

	if (!isDateRangeSelectionRelative(selection)) {
		return value;
	}

	const valueStart = value[0];
	const valueEnd = value[1];
	const numberOfDays = getNumberOfDays(value);
	let timeStart: Date = valueStart ? new Date(valueStart) : getStartOfDay(0);
	let timeEnd: Date = valueEnd ? new Date(valueEnd) : startOfEndDay ? getStartOfDay(0) : getEndOfDay(0);
	let dtEnd: Date = getStartOfDay();
	let dtStart: Date = getStartOfDay();

	if (selection === DateRangeSelection.FUTURE_NUMBER_OF_DAYS) {
		dtEnd = addDaysToDate(dtEnd, numberOfDays);
		if (isolateDay) {
			dtStart = addDaysToDate(dtStart, numberOfDays);
		}
	} else if (selection === DateRangeSelection.PAST_NUMBER_OF_DAYS) {
		dtStart = addDaysToDate(dtStart, -numberOfDays);
		if (isolateDay) {
			dtEnd = addDaysToDate(dtEnd, -numberOfDays);
		}
	} else if (selection === DateRangeSelection.TOMORROW) {
		dtEnd.setDate(dtEnd.getDate() + 1);
		if (isolateDay) {
			dtStart.setDate(dtStart.getDate() + 1);
		}
	} else if (selection === DateRangeSelection.YESTERDAY) {
		dtStart.setDate(dtStart.getDate() - 1);
		if (isolateDay) {
			dtEnd.setDate(dtEnd.getDate() - 1);
		}
	} else if (selection === DateRangeSelection.PAST_WEEK) {
		dtStart.setDate(dtStart.getDate() - 7);
		if (isolateDay) {
			dtEnd.setDate(dtEnd.getDate() - 7);
		}
	} else if (selection === DateRangeSelection.FUTURE_WEEK) {
		dtEnd.setDate(dtEnd.getDate() + 7);
		if (isolateDay) {
			dtStart.setDate(dtStart.getDate() + 7);
		}
	} else if (selection === DateRangeSelection.PAST_MONTH) {
		dtStart.setMonth(dtStart.getMonth() - 1);
		if (isolateDay) {
			dtEnd.setMonth(dtEnd.getMonth() - 1);
		}
	} else if (selection === DateRangeSelection.FUTURE_MONTH) {
		dtEnd.setMonth(dtEnd.getMonth() + 1);
		if (isolateDay) {
			dtStart.setMonth(dtStart.getMonth() + 1);
		}
	} else if (selection === DateRangeSelection.PAST_24_HOURS) {
		dtEnd = new Date();
		timeEnd = dtEnd;
		dtStart = new Date(dtEnd);
		dtStart.setDate(dtStart.getDate() - 1);
		timeStart = dtStart;
	}

	return [
		combineDateAndTime(dtStart, timeStart)
			.getTime(),
		combineDateAndTime(dtEnd, timeEnd)
			.getTime(),
		selection,
	] as C8DateRangeArray;
}

/**
 * Register date and time pattern, firstdayOfweek
 */
export function registerDateLocaleProperties(locale: string, datePattern: string, timePattern: string): void {
	_locale = locale;
	loadDateFnsLocale(locale);
	_datePattern = datePattern;
	_timePattern = timePattern;
	_firstDayOfWeek = getFirstDayOfWeek(_locale);

	// if user specified the patterns for Date/Time in C8, use it
	_timeFormatOptions.Date.datePattern = _datePattern;
	_timeFormatOptions.UniversalDate.datePattern = _datePattern;
	_timeFormatOptions.UniversalTime.timePattern = _timePattern;
	_timeFormatOptions.Time.timePattern = _timePattern;

	_timeFormatOptions.DateTime.datePattern = _datePattern;
	_timeFormatOptions.DateTime.timePattern = _timePattern;
	_timeFormatOptions.DateTimeSeconds.datePattern = _datePattern;
	_timeFormatOptions.DateTimeSeconds.timePattern = _timeWithSecondPattern;
	_timeFormatOptions.UniversalDateTime.datePattern = _datePattern;
	_timeFormatOptions.UniversalDateTime.timePattern = _timePattern;

	for (const key in _timeFormatOptions) {
		if (_timeFormatOptions[key]) {
			_timeFormatOptions[key].locale = _locale;
		}
	}
}

/**
 * Return the first day of the week based on the locale
 */
function getFirstDayOfWeek(loc: string): WeekStartsOn {
	const locale: any = new Intl.Locale(loc);
	if (locale.weekInfo) {
		return ((locale.weekInfo?.firstDay ?? 0) % 7) as WeekStartsOn;
	} else {
		return firstDayOfWeekLocaleMap[loc] as WeekStartsOn || 0;
	}
}

/**
 * show full year on dates
 */
export function showFullYear(): void {
	_showFullYear = true;
	if (_datePattern.indexOf("yyyy") < 0) {
		_datePattern = _datePattern.replace("yy", "yyyy");
	}

	_timeFormatOptions.Date.fullYear = true;
	_timeFormatOptions.DateTime.fullYear = true;
}

/**
 * Return default format for specified locale and Intl format e.g FORMAT_DATE_SHORT, FORMAT_SAME_YEAR
 */
export function getDefaultDateTimeFormat(localeName: string, formatName: string): string {
	return getPattern(localeName, formatOptionINTL[formatName]);
}

/**
 * Return the pattern for format e.g FORMAT_DATE_SHORT, FORMAT_SAME_YEAR
 */
export function getFormatPattern(formatItem: string): string {
	return getPattern(_locale, formatOptionINTL[formatItem]);
}

/**
 * Convert the centric locale to the standard locale e.g ( en_GB to en-GB)
 */
export function convertCentricToStandardLocale(locale: string): string {
	return locale.replace(/_/g, "-");
}

/**
 * Checks if the epoch date is in second based on year of a given date is after the year 2000.
 * @param {number} dateValue - epoch timestamp.
 * @returns {boolean} True if the year is before or equal 2000, false otherwise.
 */
export function isEpochDateInSecond(dateValue: number): boolean {
	const date = new Date(dateValue);
	return date.getUTCFullYear() <= 2000;
}

/**
 * Converts epoch date to milliseconds
 * @param {number} dateValue - epoch timestamp Second or Milliseconds.
 * @returns {number} milliseconds
 */
export function getTimeInMilliseconds(dateValue: number): number {
	return isEpochDateInSecond(dateValue) ? dateValue * 1000 : dateValue;
}

/**
 * Check to see if two given dates have same year.
 * This is different than getDifferenceInYear which checks full 365 days difference in year.
 * This function checks to see the local year is same or not.
 * @param {date} dateLeft - 1st date to compare
 * @param {date} dateRight - 2nd date to compare
 * @returns {boolean} true if local year is same.
 */
export function isSameLocalYear(dateLeft: Date, dateRight: Date): boolean {
	return dateLeft.getFullYear() === dateRight.getFullYear();
}

/**
 * Check to see if two given dates have same full date.
 * This is different than getDateDifference which does calculation to check the timezone,,
 * This function checks to see the local date is same or not.
 * @param {date} dateLeft - 1st date to compare
 * @param {date} dateRight - 2nd date to compare
 * @returns {boolean} true if local date is same
 */
export function isSameLocalDate(dateLeft: Date, dateRight: Date): boolean {
	return (dateLeft.getFullYear() === dateRight.getFullYear() && dateLeft.getMonth() === dateRight.getMonth() && dateLeft.getDate() === dateRight.getDate());
}

/**
 * Return the pattern (e.g. mm/dd/yyyy) from Intl DateTimeFormatOption
 */
function getPattern(locale: string, formatOption: Intl.DateTimeFormatOptions): string {
	const formatter: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat(locale, formatOption)
		.formatToParts(new Date(2021, 7, 1, 1, 1, 1));
	return formatter.map((e) => {
		const value = e.value.toString();
		switch (e.type) {
			case "month":
				return getMonthPattern(value);
			case "day":
				return getDayPattern(value);
			case "year":
				return getYearPattern(value);
			case "hour":
				return getHourPattern(value, isDayPeriod(formatter));
			case "minute":
				return getMinutePattern(value);
			case "second":
				return getSecondPattern(value);
			case "dayPeriod":
				return getDayPeriodPattern(value);
			case "literal":
				return value;
			case "weekday":
				return getWeekdayPattern(value);
			case "timeZoneName":
				return "XXX";
			default:
				return "";
		};
	})
		.join("");
}
/**
 * Return whether dayPeriod is true or false
 */
function isDayPeriod(formatter: Intl.DateTimeFormatPart[]): boolean {
	return formatter.find((item) => {
		return item.type === "dayPeriod";
	}) ? true : false;
}

/**
 * Return the month pattern
 */
function getMonthPattern(value: string): string {
	if (value.length == 1) {
		return "M";
	} else if (value.length == 2) {
		return "MM";
	} else if (value.length == 3) {
		return "MMM";
	} else if (value.length > 3) {
		return "MMMM";
	}
}

/**
 * Return the day pattern
 */
function getDayPattern(value: string): string {
	if (value.length == 1) {
		return "d";
	} else if (value.length == 2) {
		return "dd";
	}
}

/**
 * Return the Year pattern
 */
function getYearPattern(value: string): string {
	if (value.length == 2) {
		return "yy";
	} else if (value.length == 4) {
		return "yyyy";
	}
}

/**
 * Return the Hour pattern
 */
function getHourPattern(value: string, isDayPeriod: boolean): string {
	if (value.length == 1) {
		if (isDayPeriod) {
			return "h";
		} else {
			return "H";
		}
	} else if (value.length == 2) {
		if (isDayPeriod) {
			return "hh";
		} else {
			return "HH";
		}
	}
}

/**
 * Return the Minute pattern
 */
function getMinutePattern(value: string): string {
	if (value.length == 1) {
		return "m";
	} else if (value.length == 2) {
		return "mm";
	}
}

/**
 * Return the Second pattern
 */
function getSecondPattern(value: string): string {
	if (value.length == 1) {
		return "s";
	} else if (value.length == 2) {
		return "ss";
	}
}

/**
 * Return the day period pattern
 */
function getDayPeriodPattern(value: string): string {
	return "a";
}

/**
 * Return the week day pattern
 */
function getWeekdayPattern(value: string): string {
	if (value.length == 1) {
		return "EEEEE";
	} else if (value.length == 2) {
		return "EEEEEE";
	} else if (value.length == 3) {
		return "E..EEE";
	} else if (value.length > 3) {
		return "EEEE";
	}
}